
import React from 'react';

interface Deal {
  description: string;
  claimed: number;
  expiresAt: string;
}

interface DealInfoProps {
  deal: Deal;
}

/**
 * DealInfo – displays basic information about a deal.
 *
 * The component now includes simple runtime validation to avoid crashes
 * when the `deal` prop is missing or contains unexpected values.
 */
export default function DealInfo({ deal }: DealInfoProps) {
  // Defensive programming: ensure we have a valid object before accessing its fields
  if (!deal) {
    console.error('DealInfo: `deal` prop is required.');
    return null;
  }

  const { description = '', claimed = 0, expiresAt = '' } = deal;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">About This Deal</h2>
      <p className="text-sm text-gray-600 leading-relaxed mb-6">{description}</p>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-group-line text-teal-700 text-base"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Claimed</p>
            <p className="text-sm font-bold text-gray-900">{claimed} people</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
          <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-amber-700 text-base"></i>
          </div>
          <div>
            <p className="text-xs text-gray-500">Expires</p>
            <p className="text-sm font-bold text-gray-900">{expiresAt}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
